import {createNoAuthenticationClient, createClient} from "@/http-client";
import { jwtDecode } from "jwt-decode";
import {RegistrationUser} from "@/models/RegistrationUser";
import { TokenLoginResult } from "@/models/TokenLoginResult";
import { useLocalStorageStore } from "@/stores/localStorage";
import {prefixBaseUrlOneApi} from "@/helper";

let refreshTokenInFlight = false;

export class AuthenticationService {

    private app;

    constructor(app) {
        this.app = app;
    }

    async getAccessToken(): Promise<string | null> {
        if (localStorage.getItem("auth_scope") === "candidate")
            return localStorage.getItem('access_token');
        else
            return null;
    }

    isAuthenticated(): boolean {

        if (localStorage.getItem("auth_scope") !== "candidate")
            return false;

        const decoded = this.getDecodedToken();
        if (!decoded)
            return false;

        if (Date.now() >= decoded.exp * 1000) {
            if (this.app && this.app.appContext) {
                this.app.appContext.config.globalProperties.$auth.setUser(null);
            }
            return false;
        }

        return true;
    }

    getDecodedToken() {
        const token = localStorage.getItem('access_token');
        if (!token || token == "null")
            return false;

        return jwtDecode(token) as any;
    }

    openLoginDialog(options: any | null) {
        this.app.appContext.config.globalProperties.$auth.openLoginDialog(null, options);
    }

    openRegistrationDialog(options: any | null) {
        this.app.appContext.config.globalProperties.$auth.openRegistrationDialog(null, options);
    }

    updateFlowState() {
        this.app.appContext.config.globalProperties.$auth.updateFlowState();
    }

    ensureLoggedIn(action: () => any, loginOptions: any | null) {
        if (this.isAuthenticated()) {
            action();
        } else {
            this.app.appContext.config.globalProperties.$auth.openLoginDialog(action, loginOptions);
        }
    }

    async changePassword(app, password) {
        const user = this.getUser();
        const client = await createClient(app);
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/change-password", {
            user: user?.email,
            password: password
        });
        return response.data;
    }

    async changeEmail(app, newEmail) {
        const user = this.getUser();
        const client = await createClient(app);
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/change-email", {
            oldEmail: user?.email,
            newEmail: newEmail
        });
        return response.status === 200;
    }

    async recoverPassword(user) {
        const client = await createNoAuthenticationClient();
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/recover-password", {user: user});
        return response.data;
    }

    async loginWithPassword(user, password) {
        const client = await createNoAuthenticationClient();
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/login", {user: user, password: password});
        const tokens = response.data;
        if (response.status === 200 && tokens !== null) {
            localStorage.setItem('access_token', tokens.accessToken);
            localStorage.setItem('refresh_token', tokens.refreshToken);
            localStorage.setItem('auth_scope', "candidate");
        } else {

            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem("auth_scope");

        }
        this.updateAuthState();
        return this.isAuthenticated();
    }

    async loginAfterVerification(token): Promise<string|null> {
        const client = await createNoAuthenticationClient();
        const response = await client.post<TokenLoginResult>(prefixBaseUrlOneApi + "/kandidaten/authentication/login-after-verification", {token: token});
        if (response.status === 200) {
            const result = response.data;
            if (result != null) {
                if (result.tokens != null) {
                    localStorage.setItem('access_token', result.tokens.accessToken);
                    localStorage.setItem('refresh_token', result.tokens.refreshToken);
                    localStorage.setItem('auth_scope', "candidate");
                }
                if (result.brandId) {
                    const scopeKey = useLocalStorageStore().getScopeKeyFromNumber(result.brandId);
                    if (scopeKey !== null) useLocalStorageStore().setScope(scopeKey)
                }
            }

            return result?.email;
        }
        return null;
    }

    private refreshTokenInFlight = false;

    async refreshToken() {
        if (refreshTokenInFlight)
            return "already_requested";

        const client = await createNoAuthenticationClient();
        const refreshToken = localStorage.getItem('refresh_token');

        if (refreshToken == null)
            return false;

        refreshTokenInFlight = true;
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/refresh-token", {refreshToken: refreshToken});
        refreshTokenInFlight = false;

        const tokens = response.data;
        if (response.status === 200 && tokens !== null && tokens.accessToken !== null) {
            localStorage.setItem('access_token', tokens.accessToken);
            localStorage.setItem('refresh_token', tokens.refreshToken);

            return true;
        } else
            return false;
    }

    getUser() {
        if (!this.isAuthenticated())
            return null;
        const decoded = this.getDecodedToken();
        return {
            "email": decoded['http://' +
            'sche' +
            'mas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
            "email_verified": decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailverified"]
        };
    }

    updateAuthState() {
        const user = this.getUser();
        if (user)
            this.app.appContext.config.globalProperties.$auth.setUser(user);
        else
            this.app.appContext.config.globalProperties.$auth.setUser(null);
    }

    setUserShouldChangePassword(newValue) {
        this.app.appContext.config.globalProperties.$auth.userShouldChangePassword = newValue;
    }

    userShouldChangePassword() {
        return this.app.appContext.config.globalProperties.$auth.userShouldChangePassword;
    }

    async signUpWithPassword(userData: RegistrationUser) {
        const client = await createNoAuthenticationClient();
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/sign-up",
            {
                encryptedCandidateId: userData.encryptedCandidateId,
                user: userData.emailAddress,
                password: userData.password,
                brandId: userData.brandId,
                returnUrl: window.location.href,
                source: userData.source
            },
            {
                validateStatus: status => {
                    return status < 500;
                }
            });
        if (response.status === 200) {
            await this.loginWithPassword(userData.emailAddress, userData.password);
            return true;
        }
        return null;
    }

    async signOut(): Promise<any> {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        this.updateAuthState();
        sessionStorage.removeItem("dashboard_visited");
    }

    async getUserEmail(): Promise<string | undefined | null> {
        if (!this.isAuthenticated())
            return null;
        return this.getDecodedToken()['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'];
    }

    async getRequestOptions(method = 'GET') {
        const accessToken = await this.getAccessToken();

        return {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken
            }
        }
    }

    async emailAlreadyExists(emailAddress: string) {
        const client = await createNoAuthenticationClient();
        const response = await client.post(prefixBaseUrlOneApi + "/kandidaten/authentication/check",
            {
                user: emailAddress,
                password: null
            },
            {
                validateStatus: status => {
                    return status < 500;
                }
            });
        return response.status === 200;


    }
}

export function createAuthenticationService(app): AuthenticationService {
    return new AuthenticationService(app);
}

